/**
 * Procurement React hooks (S-126-04 worksheet-driven path).
 * TanStack Query bindings for ProcurementRequest list + create.
 */

import {
  createProcurementRequest,
  createSalesProcurementRequest,
  listProcurementRequests,
  markProcurementGroupOrdered,
  submitProcurementCart,
  updateProcurementRequest,
  type CreateProcurementRequestInput,
  type MarkGroupOrderedInput,
  type ProcurementRequest,
  type ProcurementRequestFilter,
  type SalesProcurementInput,
  type UpdateProcurementRequestInput,
} from '@/api/procurement';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const LIST_STALE_MS = 30_000;

export function useProcurementRequests(filter: ProcurementRequestFilter = {}) {
  const query = useQuery({
    queryKey: ['procurement-requests', filter],
    queryFn: async () => {
      // #1034 follow-up: a TELJES választ tartjuk meg, nem csak a `data`-t. A `meta`
      // eldobása némává tenné a lapozást: a lista rövidebb lenne, és semmi nem jelezné.
      const res = await listProcurementRequests(filter);
      return res;
    },
    staleTime: LIST_STALE_MS,
  });

  const meta = query.data?.meta;

  return {
    requests: (query.data?.data ?? []) as ProcurementRequest[],
    /** Lapozás-metaadat, ha a BE küldi (#1034). `undefined` = nincs lapozás. */
    meta,
    /** `true`, ha a lista CSONKÍTOTT — a UI-nak jeleznie KELL. */
    isTruncated: meta?.hasMore === true,
    isLoading: query.isLoading,
    error: query.error?.message ?? null,
    refetch: query.refetch,
  };
}

export function useCreateProcurementRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProcurementRequestInput) => createProcurementRequest(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['procurement-cart'] });
    },
  });
}

/**
 * Mutation hook for state-machine transitions + quantity/notes edits.
 * Backend Q-126-03 ratify state-machine (Sprint 23 W0 D-2):
 *   DRAFT     → SUBMITTED | CANCELLED
 *   SUBMITTED → ORDERED   | CANCELLED
 *   ORDERED   → RECEIVED
 *   RECEIVED, CANCELLED → terminal
 * Invalid transitions return 422 PROCUREMENT_INVALID_TRANSITION.
 */
export function useUpdateProcurementRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProcurementRequestInput }) =>
      updateProcurementRequest(id, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] });
    },
  });
}

/**
 * Mutation hook for the SALES-driven "POS nincs raktáron → kosárba" flow
 * (S-126-05 slice 1). Wraps the convenience `createSalesProcurementRequest`
 * helper and invalidates the same caches as the generic create hook.
 *
 * Use this in POS pages when adding a missing item to the procurement cart;
 * use `useCreateProcurementRequest` directly when the source isn't SALES.
 */
export function useCreateSalesProcurementRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SalesProcurementInput) => createSalesProcurementRequest(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['procurement-cart'] });
    },
  });
}

/**
 * S-126-08 — Bulk cart-submit hook. Moves every DRAFT request in the
 * current tenant's cart → SUBMITTED and flips cart status → SUBMITTED.
 */
export function useSubmitProcurementCart() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => submitProcurementCart(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['procurement-cart'] });
    },
  });
}

/**
 * S-126-08 — Bulk mark-group-as-ordered hook with supplier-routing free-text.
 */
export function useMarkProcurementGroupOrdered() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: MarkGroupOrderedInput) => markProcurementGroupOrdered(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['procurement-requests'] });
      void queryClient.invalidateQueries({ queryKey: ['procurement-cart'] });
    },
  });
}
